<template>
  <div class="solution-catalog">
    <section class="solution-toolbar">
      <div class="solution-toolbar__line">
        <div v-if="showSearch" class="solution-toolbar__search">
          <el-input
            ref="searchInput"
            v-model.trim="keyword"
            clearable
            placeholder="按标题、简介或文件名搜索"
            @keyup.enter.native="fetchCatalog"
          />
        </div>

        <div class="solution-toolbar__actions">
        <el-button type="primary" icon="el-icon-search" :loading="loading" @click="toggleSearch">
          搜索
        </el-button>
        <el-button icon="el-icon-refresh" :disabled="loading" @click="resetAndFetch">
          重置
        </el-button>
        <button
          type="button"
          class="solution-filter solution-filter--all"
          :class="{ 'is-active': activeSection === '' }"
          @click="activeSection = ''"
        >
          全部
        </button>
        </div>
      </div>

      <div v-if="sections.length" class="solution-toolbar__filters">
        <button
          v-for="section in sections"
          :key="section"
          type="button"
          class="solution-filter"
          :class="{ 'is-active': activeSection === section }"
          @click="activeSection = section"
        >
          {{ section }}
        </button>
      </div>
    </section>

    <el-alert
      v-if="visibleMessage"
      :title="visibleMessage"
      type="info"
      :closable="false"
      show-icon
      class="solution-alert"
    />

    <section v-if="loading" class="solution-loading">
      <div class="solution-loading__dot"></div>
      <div class="solution-loading__dot"></div>
      <div class="solution-loading__dot"></div>
      <span>正在读取解决方案目录...</span>
    </section>

    <el-empty
      v-else-if="!displayItems.length"
      description="当前没有匹配的解决方案文章"
      class="solution-empty"
    />

    <section v-else class="solution-grid">
      <article
        v-for="item in displayItems"
        :key="item.id"
        class="solution-card"
        @click="openArticle(item)"
      >
        <div v-if="item.section" class="solution-card__top">
          <span class="solution-card__section">{{ item.section }}</span>
        </div>

        <div class="solution-card__body">
          <h3>{{ item.title }}</h3>
          <p>
            {{ item.description || '当前文章未提取到简介，可直接打开 HTML 页面查看完整内容。' }}
          </p>
        </div>

        <div class="solution-card__bottom">
          <span class="solution-card__time">{{ formatTime(item.updated_at) }}</span>
          <button type="button" class="solution-card__action" @click.stop="openArticle(item)">
            打开文章
          </button>
        </div>
      </article>
    </section>
  </div>
</template>

<script>
export default {
  name: 'SolutionCatalog',
  data() {
    return {
      loading: false,
      keyword: '',
      showSearch: false,
      activeSection: '',
      items: [],
      meta: {
        source: '',
        message: ''
      }
    }
  },
  computed: {
    visibleMessage() {
      const message = String(this.meta.message || '').trim()
      if (!message) return ''
      if (message.includes('已回退到目录扫描')) return ''
      return message
    },
    sourceLabel() {
      if (this.meta.source === 'publish-api') return '发布接口'
      if (this.meta.source === 'directory-scan') return '目录扫描'
      return '未连接'
    },
    sections() {
      const out = new Set()
      this.items.forEach((item) => {
        const section = (item.section || '').trim()
        if (section) out.add(section)
      })
      return Array.from(out)
    },
    displayItems() {
      if (!this.activeSection) return this.items
      return this.items.filter((item) => (item.section || '') === this.activeSection)
    }
  },
  created() {
    this.fetchCatalog()
  },
  methods: {
    fetchCatalog() {
      this.loading = true
      this.$axios.get('/solutions/catalog', {
        params: {
          q: this.keyword,
          limit: 200
        }
      }).then((res) => {
        const data = res.data || {}
        this.items = Array.isArray(data.items) ? data.items : []
        this.meta = data.meta || { source: '', message: '' }
        if (this.activeSection && !this.sections.includes(this.activeSection)) {
          this.activeSection = ''
        }
      }).catch((err) => {
        this.items = []
        this.meta = { source: '', message: '' }
        this.activeSection = ''
        const msg = err.response && err.response.data && err.response.data.message
          ? err.response.data.message
          : '读取解决方案目录失败'
        this.$message.error(msg)
      }).finally(() => {
        this.loading = false
      })
    },
    toggleSearch() {
      if (!this.showSearch) {
        this.showSearch = true
        this.$nextTick(() => {
          const input = this.$refs.searchInput && this.$refs.searchInput.focus
            ? this.$refs.searchInput
            : null
          if (input) input.focus()
        })
        return
      }
      this.fetchCatalog()
    },
    resetAndFetch() {
      this.keyword = ''
      this.showSearch = false
      this.activeSection = ''
      this.fetchCatalog()
    },
    openArticle(item) {
      if (!item || !item.url) {
        this.$message.warning('当前文章链接无效')
        return
      }
      const opened = window.open(item.url, '_blank')
      if (!opened) window.location.href = item.url
    },
    formatTime(value) {
      if (!value) return '时间未知'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return value
      const y = date.getFullYear()
      const m = `${date.getMonth() + 1}`.padStart(2, '0')
      const d = `${date.getDate()}`.padStart(2, '0')
      const hh = `${date.getHours()}`.padStart(2, '0')
      const mm = `${date.getMinutes()}`.padStart(2, '0')
      return `${y}-${m}-${d} ${hh}:${mm}`
    }
  }
}
</script>

<style scoped lang="less">
.solution-catalog {
  padding: 6px 0 20px;
  color: var(--text-primary, #303133);
}

.solution-hero {
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1.35fr) minmax(300px, 0.9fr);
  gap: 18px;
  padding: 24px;
  border-radius: 22px;
  overflow: hidden;
  background:
    radial-gradient(circle at top left, rgba(0, 166, 214, 0.22) 0%, rgba(0, 166, 214, 0) 36%),
    radial-gradient(circle at bottom right, rgba(10, 95, 215, 0.2) 0%, rgba(10, 95, 215, 0) 34%),
    linear-gradient(135deg, #f8fbff 0%, #eef6ff 54%, #f7fbff 100%);
  border: 1px solid rgba(78, 134, 255, 0.12);
  box-shadow: 0 20px 44px rgba(73, 126, 195, 0.12);
}

.solution-hero__copy {
  position: relative;
  z-index: 1;

  h2 {
    margin: 8px 0 10px;
    font-size: 31px;
    line-height: 1.15;
    letter-spacing: 0.01em;
  }

  p {
    margin: 0;
    max-width: 680px;
    color: var(--text-regular, #606266);
    font-size: 14px;
    line-height: 1.78;
  }
}

.solution-hero__eyebrow {
  display: inline-flex;
  align-items: center;
  padding: 7px 12px;
  border-radius: 999px;
  background: rgba(20, 108, 255, 0.1);
  color: #1d69f3;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.solution-hero__stats {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  align-self: end;
}

.solution-stat {
  min-height: 96px;
  padding: 16px 14px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.78);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.8);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.55);
  box-sizing: border-box;

  strong {
    display: block;
    margin-bottom: 6px;
    font-size: 22px;
    color: #12355f;
    line-height: 1.15;
  }

  span {
    color: var(--text-secondary, #909399);
    font-size: 12px;
  }
}

.solution-stat--source strong {
  font-size: 18px;
}

.solution-toolbar {
  margin-top: 18px;
  padding: 16px 18px;
  border-radius: 16px;
  background: var(--bg-card, #fff);
  border: 1px solid var(--border-color, #ebeef5);
  box-shadow: 0 14px 30px rgba(15, 43, 91, 0.06);
}

.solution-toolbar__line {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.solution-toolbar__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.solution-toolbar__search {
  display: block;
  flex: 1;
  min-width: 320px;
}

.solution-toolbar__filters {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 12px;
}

.solution-filter {
  min-height: 36px;
  padding: 0 14px;
  border: 1px solid var(--border-color, #dfe6ee);
  border-radius: 999px;
  background: #f6f9fc;
  color: var(--text-regular, #606266);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: #1f77ff;
    border-color: rgba(31, 119, 255, 0.3);
    background: #edf5ff;
  }

  &.is-active {
    color: #fff;
    border-color: transparent;
    background: linear-gradient(135deg, #227cff 0%, #29b8ff 100%);
    box-shadow: 0 10px 18px rgba(34, 124, 255, 0.22);
  }
}

.solution-filter--all {
  margin-left: -2px;
}

.solution-alert {
  margin-top: 16px;
}

.solution-loading,
.solution-empty {
  margin-top: 18px;
}

.solution-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 180px;
  border-radius: 18px;
  background: var(--bg-card, #fff);
  border: 1px solid var(--border-color, #ebeef5);
  color: var(--text-regular, #606266);
}

.solution-loading__dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: linear-gradient(135deg, #227cff 0%, #29b8ff 100%);
  animation: solution-bounce 0.9s infinite ease-in-out;

  &:nth-child(2) {
    animation-delay: 0.12s;
  }

  &:nth-child(3) {
    animation-delay: 0.24s;
  }
}

.solution-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  margin-top: 16px;
}

.solution-card {
  display: flex;
  flex-direction: column;
  min-height: 236px;
  padding: 14px 14px 12px;
  border-radius: 18px;
  border: 1px solid rgba(220, 230, 241, 0.9);
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.96) 0%, rgba(246, 250, 255, 0.96) 100%);
  box-shadow: 0 14px 28px rgba(16, 41, 82, 0.06);
  cursor: pointer;
  transition: transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease;
  box-sizing: border-box;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-4px);
    border-color: rgba(34, 124, 255, 0.28);
    box-shadow: 0 18px 34px rgba(27, 90, 173, 0.12);
  }

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 3px;
    background: linear-gradient(90deg, #2b7cff 0%, #29b8ff 100%);
    opacity: 0.9;
  }
}

.solution-card__top,
.solution-card__bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.solution-card__section {
  display: inline-flex;
  align-items: center;
  max-width: 55%;
  padding: 5px 9px;
  border-radius: 999px;
  background: rgba(34, 124, 255, 0.1);
  color: #166ce8;
  font-size: 11px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.solution-card__time {
  color: var(--text-secondary, #909399);
  font-size: 11px;
  white-space: nowrap;
}

.solution-card__body {
  flex: 1;
  padding: 12px 0 14px;

  h3 {
    margin: 0 0 8px;
    color: var(--text-primary, #303133);
    font-size: 17px;
    line-height: 1.4;
    word-break: break-word;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  p {
    margin: 0;
    color: var(--text-regular, #606266);
    font-size: 13px;
    line-height: 1.7;
    word-break: break-word;
    display: -webkit-box;
    -webkit-line-clamp: 4;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
}

.solution-card__bottom {
  justify-content: space-between;
  padding-top: 10px;
  border-top: 1px dashed rgba(194, 209, 226, 0.8);
}

.solution-card__action {
  flex-shrink: 0;
  min-width: 80px;
  height: 30px;
  padding: 0 12px;
  border: 0;
  border-radius: 999px;
  background: linear-gradient(135deg, #227cff 0%, #29b8ff 100%);
  color: #fff;
  font-size: 12px;
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.18s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 10px 18px rgba(34, 124, 255, 0.22);
  }
}

@keyframes solution-bounce {
  0%, 80%, 100% {
    transform: scale(0.65);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

@media (max-width: 960px) {
  .solution-hero {
    grid-template-columns: 1fr;
  }

  .solution-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 768px) {
  .solution-catalog {
    padding: 4px 0 16px;
  }

  .solution-hero {
    padding: 18px 16px;
    border-radius: 0;
  }

  .solution-hero__copy h2 {
    font-size: 28px;
  }

  .solution-hero__stats {
    grid-template-columns: 1fr;
  }

  .solution-toolbar {
    margin-top: 12px;
    padding: 14px;
    border-radius: 0;
  }

  .solution-toolbar__search {
    min-width: 100%;
  }

  .solution-toolbar__line {
    flex-direction: column;
    align-items: stretch;
  }

  .solution-toolbar__actions {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .solution-grid {
    grid-template-columns: 1fr;
    gap: 14px;
    margin-top: 14px;
  }

  .solution-card {
    min-height: auto;
    padding: 13px 13px 11px;
  }

  .solution-card__bottom {
    align-items: center;
    flex-direction: row;
  }

  .solution-card__action {
    width: auto;
  }
}
</style>

<style lang="less">
.dark-mode .solution-catalog {
  color: var(--text-primary);
}

.dark-mode .solution-toolbar {
  background: var(--bg-card);
  border-color: var(--border-color);
  box-shadow: 0 14px 30px rgba(0, 0, 0, 0.18);
}

.dark-mode .solution-filter {
  background: var(--bg-secondary);
  color: var(--text-regular);
  border-color: var(--border-color);
}

.dark-mode .solution-filter:hover {
  color: #8cc8ff;
  border-color: rgba(64, 158, 255, 0.45);
  background: var(--hover-bg);
}

.dark-mode .solution-loading {
  background: var(--bg-card);
  border-color: var(--border-color);
  color: var(--text-regular);
}

.dark-mode .solution-card {
  border-color: var(--border-color);
  background: linear-gradient(180deg, rgba(31, 43, 60, 0.96) 0%, rgba(24, 34, 49, 0.98) 100%);
  box-shadow: 0 14px 28px rgba(0, 0, 0, 0.24);
}

.dark-mode .solution-card:hover {
  border-color: rgba(64, 158, 255, 0.36);
  box-shadow: 0 18px 34px rgba(0, 0, 0, 0.32);
}

.dark-mode .solution-card__section {
  background: rgba(64, 158, 255, 0.16);
  color: #8cc8ff;
}

.dark-mode .solution-card__bottom {
  border-top-color: var(--border-color);
}

.dark-mode .solution-empty .el-empty__description p {
  color: var(--text-secondary);
}
</style>
